"use client";

import React, { useEffect, useState, ChangeEvent, FormEvent } from "react";
import {
  Grid,
  Typography,
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Zoom,
  Snackbar,
  Alert,
  AlertColor,
  Container,
  Card,
  alpha,
} from "@mui/material";
import AnimatedBulbIcon from "./AnimatedBulbIcon";

// import AnimatedStars from "./AnimatedStars";
import Carousel from "./Carousel";
import Image from "next/image";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ChatIcon from "@mui/icons-material/Chat";
import { shades, theme } from "@/styles/theme";

interface Slide {
  title: React.ReactNode;
  description: string;
  cta?: string;
}

interface Notification {
  open: boolean;
  message: string;
  severity: AlertColor;
}

const HeroSection: React.FC = () => {
  const [mounted, setMounted] = useState<boolean>(false);
  const [notification, setNotification] = useState<Notification>({
    open: false,
    message: "",
    severity: "success",
  });

  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [consent, setConsent] = useState<boolean>(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { name, value, type, checked } = e.target;
    if (name === "consent") {
      setConsent(checked);
    } else if (name === "name") {
      setName(value);
    } else if (name === "email") {
      setEmail(value);
    } else if (name === "phone") {
      setPhone(value);
    }
  };

  const handleConsult = () => {
    window.open("https://calendly.com/linxarlabs-info", "_blank");
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (!name || !email || !phone) {
      setNotification({
        open: true,
        message: "Please fill out all fields",
        severity: "error",
      });
      return;
    }

    if (!consent) {
      setNotification({
        open: true,
        message: "Please agree to the privacy policy",
        severity: "error",
      });
      return;
    }

    try {
      const response = await fetch("/api/submitToAirtable", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, phone, consent }),
      });

      if (!response.ok) throw new Error("Failed to submit the form.");

      setNotification({
        open: true,
        message:
          "Form submitted successfully! We'll contact you within 24 hours.",
        severity: "success",
      });

      // Clear input fields
      setName("");
      setEmail("");
      setPhone("");
      setConsent(false);
    } catch (error) {
      setNotification({
        open: true,
        message: "Error submitting the form! Please try again.",
        severity: "error",
      });
      console.error("Submission error:", error);
    }
  };

  const handleClose = () => {
    setNotification({ ...notification, open: false });
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  const slides: Slide[] = [
    {
      title: (
        <>
          <Typography
            variant="body2"
            sx={{ color: "white", mb: 2, fontWeight: 600 }}
          >
            WEB DEVELOPMENT & AUTOMATION
          </Typography>
          <Typography
            variant="h1"
            sx={{ color: "white", fontSize: { xs: "2.6rem", md: "3.3rem" } }}
          >
            Your Remote Automation Partner – Business Automation & Web Systems
            for SMEs
          </Typography>
        </>
      ),
      description:
        "We help businesses streamline workflows, reduce manual tasks and scale faster through custom automation solutions and modern web development.",
      cta: "Book your free workflow audit",
    },
    {
      title: (
        <>
          <Typography
            variant="body2"
            sx={{ color: "white", mb: 2, fontWeight: 600 }}
          >
            GLOBAL SOLUTIONS
          </Typography>
          <Typography
            variant="h1"
            sx={{ color: "white", fontSize: { xs: "2.8rem", md: "3.3rem" } }}
          >
            No-code Automation & Workflow Solutions
          </Typography>
        </>
      ),
      description:
        "Trusted by businesses in Netherlands, UK, and across Europe. We understand local markets while delivering world-class digital solutions.",
      cta: "Book Discovery Call",
    },
    {
      title: (
        <>
          <Typography
            variant="body2"
            sx={{ color: "white", mb: 2, fontWeight: 600 }}
          >
            BUSINESS AUTOMATION & WEB SYSTEMS
          </Typography>
          <Typography
            variant="h1"
            sx={{ color: "white", fontSize: { xs: "2.6rem", md: "3.3rem" } }}
          >
            Automate. Scale. Grow.
            <br /> Your Remote Automation Partner for SMEs
          </Typography>
        </>
      ),
      description:
        "We design intelligent automation workflows and custom web systems that help startups and SMEs save time, reduce costs, and scale faster.",
      cta: "Book Your Free Workflow Audit",
    },
  ];

  return (
    <>
      <Box
        width="100%"
        minHeight="80vh"
        position="relative"
        sx={{
          background: `linear-gradient(135deg, ${shades.primary[400]} 0%, ${shades.secondary[400]} 100%)`,
          pt: 5,
          pb: 6,
          overflow: "hidden",
        }}
      >
        {/* Enhanced Wave Divider */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            width: "100%",
            overflow: "hidden",
            lineHeight: 0,
          }}
        >
          <svg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            style={{
              width: "120%",
              height: "120px",
              transform: "rotate(180deg)",
            }}
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              fill="#FFFFFF"
              fillOpacity="0.1"
            />
          </svg>
        </Box>

        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center" minHeight="60vh">
            <Grid
              size={{ xs: 12, md: 7 }}
              display="flex"
              alignItems="center"
              zIndex={2}
            >
              <Zoom in timeout={1500}>
                <Box mt={{ xs: 4, md: 8 }} width={"100%"}>
                  {mounted && (
                    <Carousel
                      slides={slides}
                      autoPlayTime={5000}
                      // showCTA={true}
                    />
                  )}

                  {/* Additional CTA Buttons */}
                  <Box
                    sx={{ display: "flex", gap: 2, mt: 4, flexWrap: "wrap" }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<ChatIcon />}
                      sx={{
                        bgcolor: "white",
                        color: "primary.main",
                        px: 4,
                        "&:hover": {
                          bgcolor: alpha("#fff", 0.9),
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                      onClick={handleConsult}
                    >
                      Book Discovery Call
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      href="/case-studies"
                      endIcon={<PlayArrowIcon />}
                      sx={{
                        borderColor: "white",
                        color: "white",
                        px: 4,
                        "&:hover": {
                          borderColor: "white",
                          bgcolor: alpha("#fff", 0.1),
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      View Case Studies
                    </Button>
                  </Box>
                </Box>
              </Zoom>
            </Grid>

            <Grid
              size={{ xs: 12, md: 5 }}
              display="flex"
              justifyContent="center"
              alignItems="center"
              mt={{ xs: 4, md: 0 }}
            >
              <Box
                sx={{
                  borderRadius: "50%",
                  position: "relative",
                  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
                }}
              >
                <AnimatedBulbIcon />
                {/* <AnimatedBulbIconDramatic /> */}

                <Image
                  src="/homepageHero.jpg"
                  alt="Remote automation agency for European SMEs"
                  width={350}
                  height={350}
                  sizes="(max-width: 768px) 280px, 350px"
                  style={{
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "4px solid white",
                    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
                  }}
                  priority
                />

                {/* <AnimatedStars /> */}
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Enhanced Contact Form Section */}
      <Container maxWidth="lg">
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            py: 6,
            position: "relative",
            mt: -4,
            zIndex: 1,
          }}
        >
          <Card
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.2)",
              background: `linear-gradient(135deg, ${alpha(
                shades.primary[500],
                0.09
              )} 0%, ${alpha(shades.secondary[500], 0.05)} 100%)`,
              border: `1px solid ${alpha(shades.primary[500], 0.1)}`,
            }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="h2" gutterBottom sx={{ fontWeight: 600 }}>
                  Ready to Automate Your Business?
                </Typography>
                <Typography
                  component={"h3"}
                  variant="h6"
                  color="text.secondary"
                  sx={{ mb: 2, lineHeight: 1.6 }}
                >
                  Get a free consultation with our web development and business
                  automation experts.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "success.dark",
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      Response within 24 hours
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "success.dark",
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      No commitment required
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Your Name"
                      name="name"
                      value={name}
                      onChange={handleChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={email}
                      onChange={handleChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone"
                      value={phone}
                      onChange={handleChange}
                      required
                      variant="outlined"
                    />
                  </Grid>
                  <Grid size={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="consent"
                          checked={consent}
                          onChange={handleChange}
                          required
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2" fontSize={12}>
                          I agree to have my personal details collected and
                          processed by LinXar Labs according to the{" "}
                          <Button
                            variant="text"
                            size="small"
                            sx={{
                              p: 0,
                              minWidth: "auto",
                              textDecoration: "underline",
                            }}
                            onClick={() => window.open("/privacy", "_blank")}
                          >
                            Privacy Policy
                          </Button>
                        </Typography>
                      }
                    />
                  </Grid>
                  <Grid size={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      sx={{
                        py: 1.5,
                        fontSize: "1.1rem",
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: "0 8px 25px rgba(0, 82, 255, 0.3)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Get Free Automation Consultation
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </Box>
      </Container>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleClose}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default HeroSection;
